import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { Button } from "@/components/ui/button";
import { UserMenu } from "@/components/ui/user-menu";
import { LanguageSwitcher } from "@/components/ui/language-switcher";
import {
  LayoutDashboard,
  ChefHat,
  BarChart3,
  UserCog,
  ChevronLeft,
  ChevronRight,
  Receipt,
  CreditCard,
} from "lucide-react";
import type { User as UserType } from "@/types";
interface AdminSidebarProps {
  user: UserType;
  isOpen?: boolean;
  onToggle?: () => void;
}

const buildAdminSections = (t: TFunction) => [
  {
    id: "dashboard",
    label: t("nav.dashboard"),
    icon: <LayoutDashboard className="w-5 h-5" />,
    description: t("nav.overview"),
    href: "/admin/dashboard",
    roles: ["admin", "manager", "counter"],
  },
  {
    id: "counter",
    label: t("nav.counter"),
    icon: <CreditCard className="w-5 h-5" />,
    description: t("nav.payments"),
    href: "/admin/counter",
    roles: ["admin", "manager", "counter"],
  },
  {
    id: "stock-management",
    label: t("nav.stock"),
    icon: <ChefHat className="w-5 h-5" />,
    description: t("nav.inventory"),
    href: "/admin/stock",
    roles: ["admin", "manager"],
  },
  // {
  //   id: "menu",
  //   label: t("nav.menu"),
  //   icon: <Menu className="w-5 h-5" />,
  //   description: t("nav.categories"),
  //   href: "/admin/menu",
  //   roles: ["admin", "manager"],
  // },
  // {
  //   id: "tables",
  //   label: t("nav.tables"),
  //   icon: <LayoutGrid className="w-5 h-5" />,
  //   description: t("nav.dining"),
  //   href: "/admin/tables",
  //   roles: ["admin", "manager"],
  // },
  {
    id: "staff",
    label: t("nav.staff"),
    icon: <UserCog className="w-5 h-5" />,
    description: t("nav.userManagement"),
    href: "/admin/staff",
    roles: ["admin", "manager"],
  },
  {
    id: "expenses",
    label: t("nav.expenses"),
    icon: <Receipt className="w-5 h-5" />,
    description: t("nav.expenseManagement"),
    href: "/admin/expenses",
    roles: ["admin", "manager"],
  },
  {
    id: "reports",
    label: t("nav.reports"),
    icon: <BarChart3 className="w-5 h-5" />,
    description: t("nav.analytics"),
    href: "/admin/reports",
    roles: ["admin", "manager"],
  },
  // {
  //   id: "settings",
  //   label: t("nav.settings"),
  //   icon: <UserCog className="w-5 h-5" />,
  //   description: t("nav.preferences"),
  //   href: "/admin/settings",
  //   roles: ["admin"],
  // },
];

export function AdminSidebar({ user, isOpen, onToggle }: AdminSidebarProps) {
  const { t } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const location = useLocation();

  // Filter sections based on user role
  const sections = useMemo(() => buildAdminSections(t), [t]);
  const visibleSections = useMemo(
    () => sections.filter((section) => section.roles.includes(user.role)),
    [sections, user.role]
  );

  // Responsive checks
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsTablet(width >= 768 && width < 1024);

      // Auto-collapse on smaller screens, but allow manual control
      if (width < 1024) {
        setSidebarCollapsed(true);
      }
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Use external control for mobile/tablet if provided
  const isCollapsed =
    isMobile || isTablet ? isOpen === false : sidebarCollapsed;

  const handleToggle = () => {
    if ((isMobile || isTablet) && onToggle) {
      onToggle();
    } else {
      setSidebarCollapsed(!sidebarCollapsed);
    }
  };

  const isActiveRoute = (href: string) => {
    return location.pathname === href;
  };

  return (
    <>
      {/* Backdrop for mobile - only show when sidebar is open */}
      {(isMobile || isTablet) && !isCollapsed && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={handleToggle}
        />
      )}

      {/* Sidebar */}
      <div
        className={`bg-card border-r border-border transition-all duration-300 flex flex-col z-50 h-screen ${
          isMobile || isTablet
            ? `fixed left-0 top-0 ${
                isCollapsed ? "-translate-x-full w-80" : "translate-x-0 w-80"
              }`
            : `relative ${isCollapsed ? "w-16" : "w-64"}`
        }`}
      >
        {/* Header */}
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between">
            {/* Always show logo on mobile/tablet, conditionally on desktop */}
            {(!isCollapsed || isMobile || isTablet) && (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center">
                  <img
                    src="/favicon.ico"
                    alt="guesteify logo"
                    className="w-full h-full object-contain rounded-lg"
                  />
                </div>
                <div>
                  <h1 className="font-bold text-foreground">{t("app.name")}</h1>
                  <p className="text-xs text-muted-foreground">
                    {t("app.tagline")}
                  </p>
                </div>
              </div>
            )}

            {/* Collapse/Expand Button - only show on desktop */}
            {!isMobile && !isTablet && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleToggle}
                className={`h-8 w-8 p-0 ${isCollapsed ? "mx-auto" : ""}`}
              >
                {isCollapsed ? (
                  <ChevronRight className="h-4 w-4" />
                ) : (
                  <ChevronLeft className="h-4 w-4" />
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Language Switcher - Hidden when collapsed on desktop */}
        {(!isCollapsed || isMobile || isTablet) && (
          <div className="px-4 pt-3 pb-2">
            <LanguageSwitcher variant="minimal" className="w-full" />
          </div>
        )}

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto p-4 space-y-2">
          {visibleSections.map((section) => (
            <Link key={section.id} to={section.href} className="block">
              <Button
                variant={isActiveRoute(section.href) ? "default" : "ghost"}
                className={`w-full transition-colors ${
                  isCollapsed && !isMobile && !isTablet
                    ? "justify-center px-2"
                    : "justify-start px-4"
                } ${isTablet ? "h-12 text-base" : "h-10 text-sm"}`}
                title={
                  isCollapsed && !isMobile && !isTablet
                    ? section.label
                    : undefined
                }
              >
                {section.icon}
                {(!isCollapsed || isMobile || isTablet) && (
                  <span className="ml-3">{section.label}</span>
                )}
              </Button>
            </Link>
          ))}
        </div>

        {/* User Menu */}
        <div className="p-4 border-t border-border">
          <UserMenu
            user={user}
            collapsed={isCollapsed && !isMobile && !isTablet}
            size={isTablet ? "lg" : "md"}
          />
        </div>

        {/* Powered by Verve Automation Footer */}
        {(!isCollapsed || isMobile || isTablet) && (
          <div className="px-3 pt-2 pb-4">
            <div className="flex flex-col gap-2 items-center w-full">
              {/* Guesteify Logo on top */}
              <div
                className="w-full h-12 rounded-lg flex items-center justify-center bg-white border border-border"
                onClick={() => window.open("https://guestiefy.com/", "_blank")}
              >
                <img
                  src="/favicon.ico"
                  alt="Guestiefy Logo"
                  className="w-10 h-10 object-contain"
                />
              </div>
              {/* Verve Automation Group in a rounded box */}
              <div className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-background/80 border border-border/50 rounded-lg shadow-sm transition-all">
                <div className="w-7 h-7 rounded flex items-center justify-center bg-white border border-border shrink-0">
                  <img
                    src="/VerveAutomation.png"
                    alt="Verve Automation Logo"
                    className="w-6 h-6 object-contain"
                  />
                </div>
                <div className="flex flex-col items-start justify-center leading-tight flex-1">
                  <span className="text-[11px] text-muted-foreground font-medium tracking-wide">
                    {t("app.poweredByUpper")}
                  </span>
                  <span
                    className="text-xs font-semibold text-foreground hover:text-primary underline decoration-1 underline-offset-2 cursor-pointer transition-colors"
                    onClick={() =>
                      window.open("https://verveautomation.com/", "_blank")
                    }
                  >
                    {t("app.poweredByCta")}
                  </span>
                </div>
              </div>
              <div className="text-xs text-muted-foreground/70 text-center">
                {t("app.copyright", { year: new Date().getFullYear() })}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
